import { Grid, GridColumn, Notice, Separator, Spinner, Table, TableCell } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import TranslateStore from '../../../stores/TranslateStore';
import { formatAsCurrency } from '../../../util/FormatUtil';
import WarningNotice from '../notices/WarningNotice';
import LayoutStore from '../../../stores/LayoutStore';
import InvoiceDetailsStore from '../../../stores/InvoiceDetailsStore';
import InvoiceLineUsagesDataStore from '../../../stores/data/InvoiceLineUsagesDataStore';
import InvoiceLineUsagesTableViewStore from '../../../stores/view/InvoiceLineUsagesTableViewStore';
import { InvoiceLineUsageRow } from '../../../stores/models/InvoiceLineUsageRow';
import LineUsagesEmployeeNoticeBoundary from './LineUsagesEmployeeNoticeBoundary';

interface IInvoiceLineUsagesTableProps {
  invoiceLineUsagesDataStore?: InvoiceLineUsagesDataStore;
  invoiceLineUsagesTableViewStore?: InvoiceLineUsagesTableViewStore;
  invoiceDetailsStore?: InvoiceDetailsStore;
  translateStore?: TranslateStore;
  layoutStore?: LayoutStore;
  nestingIndexes: number[];
  lineId: string;
}

@inject('invoiceLineUsagesDataStore', 'invoiceLineUsagesTableViewStore', 'invoiceDetailsStore', 'translateStore', 'layoutStore')
@observer
export default class InvoiceLineUsagesTable extends React.Component<IInvoiceLineUsagesTableProps> {
  public componentDidMount(): void {
    const invoiceNumber = this.props.invoiceDetailsStore.invoiceDetails.payload.details.invoiceHeader.invoiceNumber;
    const invoiceSource = this.props.invoiceDetailsStore.invoiceDetails.payload.details.invoiceHeader.source;
    const customerId = this.props.invoiceDetailsStore.invoiceDetailsRequest.customerId;

    this.props.invoiceLineUsagesDataStore.tryLoadInvoiceLineUsages(invoiceNumber, this.props.lineId, invoiceSource, customerId);
  }

  public render(): JSX.Element {
    const { translate, translateHtml } = this.props.translateStore;
    const invoiceNumber = this.props.invoiceDetailsStore.invoiceDetails.payload.details.invoiceHeader.invoiceNumber;
    const nestingIndexes = this.props.nestingIndexes;
    const store = this.props.invoiceLineUsagesDataStore;
    const lineUsageRows = store.invoiceLineUsages(invoiceNumber, this.props.lineId);
    const isMobile = this.props.layoutStore.isMobile;

    if (lineUsageRows == null) {
      return null;
    }

    if (lineUsageRows.isFinishedWithError) {
      return <WarningNotice translationKey="shared.invoice-details-modal.failed-to-load-invoice-details" />;
    }

    if (lineUsageRows.isFinished) {
      if (lineUsageRows.payload.length === 0) {
        return <Notice>{translate('shared.invoice-details-modal.invoice-line-usages.no-usages-notice')}</Notice>;
      }

      if (lineUsageRows.payload.some((row) => row.duration === '1' && row.serviceDescription === 'Viivis')) {
        return (
          <>
            <Separator spacing="xsmall" color="invisible" />
            <Notice size="small">
              <div dangerouslySetInnerHTML={translateHtml('shared.invoice-details-modal.invoice-line-usages.interests-notice')} />
            </Notice>
          </>
        );
      }

      return (
        <LineUsagesEmployeeNoticeBoundary>
          <Table responsive="cols">
            <tbody>
              {lineUsageRows.payload.map((row, index) => (isMobile ? this.getTableRowMobile(index, row) : this.getTableRowDesktop(index, nestingIndexes, row)))}
            </tbody>
          </Table>
        </LineUsagesEmployeeNoticeBoundary>
      );
    }

    return <Spinner />;
  }

  private getTableRowDesktop(index: number, nestingIndexes: number[], row: InvoiceLineUsageRow) {
    const tableColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableColumnWidthByColumnIndex;
    const tableLastColumnGridColumnWidthByColumnIndex = this.props.invoiceDetailsStore.tableLastColumnGridColumnWidthByColumnIndex;
    const dataRow: string = `details-row-${nestingIndexes.join('-')}-usages-${index}`;

    return (
      <tr key={dataRow} id={`invoice-${dataRow}`}>
        <TableCell
          indentLevel={this.props.invoiceDetailsStore.getIndentLevel(nestingIndexes.concat(index))}
          width={tableColumnWidthByColumnIndex(0)}
          className="text-bold"
        >
          {this.props.invoiceLineUsagesTableViewStore.getDescription(row)}
        </TableCell>
        <TableCell width={tableColumnWidthByColumnIndex(1)}>{this.props.invoiceLineUsagesTableViewStore.getDateTime(row)}</TableCell>
        <TableCell width={tableColumnWidthByColumnIndex(2)}>{this.props.invoiceLineUsagesTableViewStore.getDurationOrVolume(row)}</TableCell>
        <TableCell width={tableColumnWidthByColumnIndex(3)}>
          <Grid verticalGutter="none" gutter="xsmall" align={['between-xs']}>
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(0)} className="text-right">
              {formatAsCurrency(row.sumWoVat)}
            </GridColumn>
            <GridColumn width={tableLastColumnGridColumnWidthByColumnIndex(1)} align={['middle-xs']} />
          </Grid>
        </TableCell>
      </tr>
    );
  }

  private getTableRowMobile(index: number, row: InvoiceLineUsageRow) {
    const invoiceLineUsagesTableViewStore = this.props.invoiceLineUsagesTableViewStore;

    return (
      <tr key={index} id={`invoice-details-row-modal-usages-${index}`}>
        <TableCell colSpan={3}>
          <Grid verticalGutter="none">
            <GridColumn width={['xs-12']} className="text-bold">
              {invoiceLineUsagesTableViewStore.getDescription(row)}
            </GridColumn>
          </Grid>
          <Grid verticalGutter="none">
            <GridColumn width={['xs-7']}>{invoiceLineUsagesTableViewStore.getDateTime(row)}</GridColumn>
            <GridColumn width={['xs-3']}>{invoiceLineUsagesTableViewStore.getDurationOrVolume(row)}</GridColumn>
            <GridColumn width={['xs-2']} className="text-right">
              {formatAsCurrency(row.sumWoVat)}
            </GridColumn>
          </Grid>
        </TableCell>
      </tr>
    );
  }
}
